/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, MouseEvent, useRef} from 'react';
import {makeStyles, MenuList} from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import {translate} from '@ion/core/src/locale/locale';
import {Notifications} from '@ion/core/src/types/entities';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import DropdownItem from '@ion/dls/src/components/menus/DropdownItem';
import SingleSelectMenu from '@ion/dls/src/components/menus/SingleSelectMenu';
import {
  dlsSidebarDrawerOffset,
  dlsSidebarWidth,
  isSideNavCollapsed
} from '@ion/dls/src/components/navigation/NavigationStore';
import {useSideNavDockedDrawer} from '@ion/dls/src/components/navigation/useSideNavDockedDrawer';
import TabView from '@ion/dls/src/components/tabs/TabView';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {MaterialMoreVertIcon} from '@ion/dls/src/icons/MaterialMoreVertIcon';
import Hidden from '@ion/dls/src/layout/Hidden';
import NotificationPanelHeader from '@ion/dls/src/notifications/NotificationPanelHeader';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor, dlsShadow, dlsZIndex} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize, NotificationPanelTabs} from '@ion/dls/src/types/types';
import CloseButton from '../components/buttons/CloseButton';
import Render from '../render/Render';
import {dlsFixedTabWidth, toggleThemeStyle} from '../theme/helpers';
import useThemeContext from '../theme/useThemeContext';
import NotificationCardFooter from './NotificationCardFooter';
import NotificationOptionBar from './NotificationOptionBar';

interface NotificationDrawerProps {
  children: JSX.Element;
  closeDrawer: () => void;
  panelOpen?: boolean;
  activeTab: NotificationPanelTabs;
  onTabChange?: (value: {tabHeader: string}) => void;
  tabItems: NotificationTabType[];
  menuItems?: {
    title: string;
    handleClick: (event: MouseEvent<HTMLLIElement>) => void;
    selected?: boolean;
  }[];
  notificationMenuOpen?: boolean;
  setNotificationMenuOpen?: (val: boolean) => void;
  checkboxesEnabled?: boolean;
  selectedNotifications?: Notifications[];
  handleSelectAll?: (event: MouseEvent<HTMLButtonElement>) => void;
  onMarkSelectedAsRead?: () => void;
  onClearSelected?: () => void;
  notificationsCheckAll?: boolean;
  urgentNotifications?: any;
  allNotifications?: any;
  onSeeUnreadOnly?: () => void;
  onSeeAll?: () => void;
  filterAll?: boolean;
  filterUnread?: boolean;
}

export type NotificationTabType = {
  tab: NotificationPanelTabs;
  tabHeader: string;
}

// per Figma: 16px side padding + 8px inter-tab gap on the tab bar itself -
// the shared Tab component's own default has neither (no padding, 16px
// gap). `root`'s width is pinned with `!important` rather than relying on
// the `minWidth`/`maxWidth` props alone - those are applied as a plain
// (non-important) inline style, and something in this render tree still
// wins over that; `!important` here beats any non-important rule
// regardless of source, and neither Tab's own base style nor MUI's sets
// `!important` on any width-related property, so this is unambiguous.
// Passed via TabView's existing `overrides` mechanism so no other
// TabView caller is affected ...
const notificationTabOverrides = () => ({
  // the 2px pastelTeal baseline is drawn here (inset, so it shares the
  // tabs' own bottom 2px) instead of as the wrapper's border-bottom below
  // the tabs - that stacked it under the selected tab's darkTeal indicator
  // as a second line, while Figma ("Tabs/Selected/MWeb") shows one line
  // with the indicator overlapping it. This covers the gap/padding areas;
  // each unselected tab draws the same resting line itself (its own
  // background would otherwise hide this one) ...
  tabContainer : {
    gap       : '8px',
    padding   : '0 16px',
    boxShadow : `inset 0 -2px 0 ${dlsColor('pastelTeal')}`
  },
  // MUI's own base `Tabs.root` class sets a non-important `minHeight:48`,
  // uncontested by TabView.tsx's own `tabRoot` (an empty override class) -
  // shrinking just the individual Tab below (via `root`'s own 44px override)
  // without also shrinking the `<Tabs>` root itself left the tab's own
  // white background showing for the leftover ~4px under its indicator,
  // reading as a stray white line below the highlighted tab ...
  tabRoot : {
    minHeight : '44px !important'
  },
  root : {
    ...dlsFixedTabWidth(208),
    // this repo's own Tab.tsx wrapper doesn't forward the `textColor` prop
    // MUI's Tabs clones onto it, so it silently defaults to MUI's own
    // textColor='inherit' - whose base style bakes in opacity:0.7 when
    // unselected. That dims both this tab's text AND the badge nested
    // inside it (opacity composites the whole subtree), reading as gray
    // text and an orange-ish badge even though the declared colors are
    // black/red. Figma differentiates selected vs unselected purely by
    // color/weight, not opacity, so neutralize MUI's dimming outright ...
    opacity   : '1 !important',
    // shrinks Tab's own current-theme root from its shared 48px down to
    // this bar's 44px per Figma - this `className` lands on the exact same
    // DOM node as Tab.tsx's own `withStyles`-injected root class (both are
    // merged onto the underlying MaterialTab), so `!important` here beats
    // its non-important 48px/minHeight without touching Tab.tsx itself.
    // Needed so the selected tab's own border-bottom (drawn at the bottom
    // edge of root) lands at 44px instead of 48px - notificationTabView's
    // wrapper is exactly 44px tall, so a 48px-tall tab's own border-bottom
    // fell outside it and never rendered ...
    height    : '44px !important',
    minHeight : '44px !important'
  },
  // tabLabelContainer directly wraps the tab's text + badge - it's more
  // specific than root, so its own declarations win over whatever root
  // (or MUI's base Tab style, e.g. its uppercase-by-default typography)
  // would otherwise cascade down via inheritance, regardless of !important
  // anywhere on root ...
  tabLabelContainer : {
    fontFamily    : 'Roboto',
    fontWeight    : 500,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    textTransform : 'capitalize'
  }
});

const useStyles = makeStyles((theme: DLSTheme) => ({
  notificationDrawerRoot : {
    [theme.breakpoints.down('sm')] : {
      zIndex : `${dlsZIndex('notificationDrawerRoot')} !important`
    },
    [theme.breakpoints.up('md')] : {
      '&&' : {
        zIndex : `${dlsZIndex('notificationDrawerRoot')} !important`
      }
    }
  },
  notificationDrawerPaper : {
    width                          : '414px',
    overflow                       : 'hidden',
    [theme.breakpoints.down('sm')] : {
      width  : 'auto',
      height : '100vh'
    }
  },
  currentDrawerPaper : {
    left                           : ({collapsed}: {collapsed: boolean}) => dlsSidebarDrawerOffset(collapsed),
    right                          : 'auto !important',
    width                          : '456px',
    overflow                       : 'hidden',
    boxShadow                      : dlsShadow(1),
    // this drawer is anchored left (flush against the viewport's left
    // edge), so only the right corners - the edge actually visible facing
    // into the page - should be rounded; the left corners stay sharp ...
    borderRadius                   : '0 12px 12px 0',
    // mobile gets '&&' (doubled specificity) rather than a plain override,
    // since left's desktop value above is a dynamic (collapsed-dependent)
    // function value with !important - JSS can emit dynamic values into a
    // higher-priority stylesheet, so a plain override isn't guaranteed to
    // win against it, and a non-important rule never beats an important
    // one regardless of specificity, so left also needs !important here ...
    [theme.breakpoints.down('sm')] : {
      '&&' : {
        width  : '100vw',
        left   : '0px !important',
        height : '100vh'
      }
    }
  },
  notificationMenuRoot : {
    [theme.breakpoints.down('sm')] : {
      zIndex : `${dlsZIndex('notificationDrawerRoot')} !important`
    }
  },
  profileTitle : toggleThemeStyle({
    textTransform : 'uppercase',
    fontWeight    : 500,
    letterSpacing : '1px'
  }, {
    color         : dlsColor('grey1'),
    fontFamily    : 'Inter',
    fontSize      : '16px',
    fontWeight    : 600,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  notificationPanelHeader : toggleThemeStyle({
    display                        : 'flex',
    justifyContent                 : 'space-between',
    alignItems                     : 'center',
    padding                        : '0 24px',
    [theme.breakpoints.down('sm')] : {
      margin : '0 -15px'
    }
  }, {
    gap                            : '10px',
    height                         : '56px',
    display                        : 'flex',
    alignItems                     : 'center',
    paddingLeft                    : '20px',
    [theme.breakpoints.down('sm')] : {
      margin : '0 -15px'
    }
  }),
  notificationPanelTitle : {
    fontWeight    : 500,
    textTransform : 'uppercase',
    letterSpacing : '1px'
  },
  notificationTabView : toggleThemeStyle({
    margin : '0'
  }, {
    // matches notificationTabOverrides' own 44px root override below, so
    // the selected tab's own indicator lands flush with this wrapper's
    // bottom edge instead of past it - this wrapper draws no baseline of
    // its own, since only the active tab's own indicator should show ...
    height : '44px'
  }),
  mobileNotificationsCloseIcon : {
    display : 'flex'
  },
  hiddenMenu : {
    visibility : 'hidden'
  },
  notificationMenuMobilePaper : {
    borderTopLeftRadius  : '8px',
    borderTopRightRadius : '8px',
    padding              : '28px 0'
  },
  closeIcon : {
    marginTop : '5px'
  },
  mobileMenuItem : {
    '&:nth-of-type(3)' : {
      borderBottom : ({menuItems}: {menuItems: any[]}) =>
        menuItems.length > 3 ? `solid 1px ${dlsColor('grey5')}` : 'none'
    }
  },
  currentCloseButton : {
    left     : '270px',
    position : 'relative'
  },
  notificationOptionBarView : {
    height          : '56px',
    backgroundColor : dlsColor('grey8'),
    // this div only renders when isCurrentTheme (see Render.If below), so
    // this is already effectively current-theme-only ...
    borderBottom    : `2px solid ${dlsColor('pastelTeal')}`
  },
  notificationDrawerBody : {
    height        : '100%',
    display       : 'flex',
    minHeight     : '100%',
    flexDirection : 'column'
  },
  notificationDrawerContent : {
    flex      : '1 1 auto',
    minHeight : 0,
    overflowX : 'hidden',
    overflowY : 'auto'
  }
}));

export const NotificationDrawer = ({
  children,
  panelOpen = false,
  activeTab,
  closeDrawer,
  onTabChange,
  tabItems,
  menuItems,
  onSeeUnreadOnly,
  onSeeAll,
  allNotifications,
  urgentNotifications,
  notificationMenuOpen,
  setNotificationMenuOpen,
  checkboxesEnabled = false,
  handleSelectAll,
  selectedNotifications = [],
  onMarkSelectedAsRead,
  onClearSelected,
  filterAll = false,
  filterUnread = false,
  notificationsCheckAll = false
}: NotificationDrawerProps) => {
  const collapsed    = isSideNavCollapsed.value;
  const classes      = useStyles({menuItems, collapsed});
  const dockedDrawer = useSideNavDockedDrawer(dlsSidebarWidth(collapsed));
  const {isPhone}    = useMedia();
  const moreIconRef  = useRef(null);

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  function handleTabChange(e: ChangeEvent<{}>, value: number) {
    if (onTabChange) onTabChange(tabItems[value]);
  }

  function onNotificationMenuClick() {
    setNotificationMenuOpen?.(!notificationMenuOpen);
  }

  function handleMenuClose() {
    setNotificationMenuOpen?.(false);
  }

  return (
    <Drawer open={panelOpen}
      anchor={isPhone ? 'bottom' : (isCurrentTheme ? 'left' : 'right')}
      classes={{
        root  : classes.notificationDrawerRoot,
        paper : isCurrentTheme ? classes.currentDrawerPaper : classes.notificationDrawerPaper
      }}
      className={dockedDrawer.className}
      disableEnforceFocus={dockedDrawer.disableEnforceFocus}
      BackdropProps={{className: dockedDrawer.backdropClassName}}
      onClose={closeDrawer}>
      <div className={isCurrentTheme ? classes.notificationDrawerBody : ''}>
        <div className={isCurrentTheme ? classes.notificationDrawerContent : ''}>
          <div className={classes.notificationPanelHeader}>
            {(isPhone && isLegacyTheme) &&
            <>
              <div className={classes.mobileNotificationsCloseIcon} onClick={closeDrawer}>
                <MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />
              </div>
              <h2 className={classes.notificationPanelTitle} style={{fontSize: '20px'}}>
                {translate('common:notifications.title')}
              </h2>
            </>}
            {(!isPhone || isCurrentTheme) &&
            <>
              <Render.If condition={isLegacyTheme}>
                <div className={classes.closeIcon}>
                  <IconButton icon={<MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />}
                    onClick={closeDrawer} />
                </div>
              </Render.If>
              <h2 className={classes.profileTitle}>
                {translate('common:notifications.title')}
              </h2>
              <Render.If condition={isCurrentTheme}>
                <div className={classes.currentCloseButton}>
                  <CloseButton onClick={closeDrawer} />
                </div>
              </Render.If>
            </>
            }
            <div className={!menuItems?.length ? classes.hiddenMenu : null}>
              <Render.If condition={isLegacyTheme}>
                <div ref={moreIconRef}>
                  <FloatingIconButtonGroup.FloatingIconButton onClick={onNotificationMenuClick} color={dlsColor('grey3')}>
                    <MaterialMoreVertIcon />
                  </FloatingIconButtonGroup.FloatingIconButton>
                </div>
              </Render.If>
              <Hidden show='desktop'>
                <SingleSelectMenu menuOpen={notificationMenuOpen}
                  items={menuItems}
                  anchorEl={moreIconRef.current}
                  onMenuClose={handleMenuClose} />
              </Hidden>
              <Hidden show='mobile'>
                <Drawer anchor='bottom'
                  open={notificationMenuOpen}
                  onClose={handleMenuClose}
                  ModalProps={{
                    keepMounted : true
                  }}
                  classes={{
                    paper : classes.notificationMenuMobilePaper,
                    root  : classes.notificationMenuRoot
                  }}>
                  <MenuList>
                    {
                      menuItems?.map((item, index) => {
                        return (
                          <div key={`single-select-menu-item-${index}`} className={classes.mobileMenuItem}>
                            <DropdownItem text={item.title}
                              onClick={item.handleClick}
                              checked={item.selected} />
                          </div>
                        );
                      })
                    }
                  </MenuList>
                </Drawer>
              </Hidden>
            </div>
          </div>
          <ConditionalRender condition={selectedNotifications.length !== 0}>
            <NotificationPanelHeader selectedRows={selectedNotifications}
              count={selectedNotifications.length}
              notificationsCheckAll={notificationsCheckAll}
              onBulkDelete={onClearSelected}
              handleSelectAll={handleSelectAll}
              onBulkAcknowledge={onMarkSelectedAsRead} />
          </ConditionalRender>
          <div className={classes.notificationTabView}>
            <TabView tabItems={tabItems}
              onChange={handleTabChange}
              allNotifications={allNotifications}
              urgentNotifications={urgentNotifications}
              value={activeTab}
              overrides={isCurrentTheme ? notificationTabOverrides : undefined} />
          </div>
          <Render.If condition={isCurrentTheme}>
            <div className={classes.notificationOptionBarView}>
              <NotificationOptionBar onSeeAll={onSeeAll}
                value={activeTab}
                filterAll={filterAll}
                filterUnread={filterUnread}
                handleSelectAll={handleSelectAll}
                onSeeUnreadOnly={onSeeUnreadOnly}
                allNotifications={allNotifications}
                selectedRows={selectedNotifications}
                urgentNotifications={urgentNotifications}
                notificationsCheckAll={notificationsCheckAll} />
            </div>
          </Render.If>
          {children}
        </div>
        <Render.If condition={isCurrentTheme}>
          <NotificationCardFooter />
        </Render.If>
      </div>
    </Drawer>
  );
};

export default NotificationDrawer;
